import type { MicrospaceAnalysisTranslations } from "../en/MicrospaceAnalysis";
import type { TranslationShape } from "../../store/types";

export const microspaceAnalysisEs: TranslationShape<MicrospaceAnalysisTranslations> = {
  breadcrumb: {
    parent: "Análisis de Rendimiento",
    current: "Microespacio",
  },
  pageTitle: "Análisis de Rendimiento",
  pageSubtitle:
    "Analice el rendimiento por categoría, identifique acciones y genere simulaciones basadas en recomendaciones de IA",
  loadingStatus: {
    analysis: "Cargando datos del análisis…",
    waitingForConnection: "Esperando conexión…",
    guidance: "Las selecciones de muchas tiendas pueden tardar unos 15 minutos en cargarse.",
    elapsed: "Tiempo transcurrido: {{elapsed}}",
    progressLabel: "Progreso de carga estimado",
    estimatedProgress: "Progreso estimado: {{percent}}%",
    complete: "Carga completada",
    error: "No se pudieron cargar algunos datos.",
    retry: "Reintentar solicitudes fallidas",
  },
  configPrompt: {
    label: "Guardar Configuración",
    default: "Config",
    confirm: "Guardar",
    cancel: "Cancelar",
  },
  bannerTitle: "Descubre Insights Profundos de Categoría",
  bannerDescription:
    "Comienza configurando tus filtros y definiendo el alcance de tus tiendas, individualmente o por cluster. Guarda tus ajustes para uso futuro, profundiza en el análisis de rendimiento de tu categoría y explora recomendaciones impulsadas por IA. Cuando estés listo, genera una simulación de surtido para poner los insights en acción.",
  buttons: {
    download: "Descargar",
    changeConfiguration: "Cambiar configuracion",
    generateAnalysis: "Generar Análisis",
    generateSimulation: "Generar Simulación de Surtido",
    generating: "Generando...",
    exportActionPlan: "Exportar Plan de Acción",
    clearFilters: "Limpiar filtros",
  },
  filters: {
    granularity: "Granularidad",
    year: "Año",
    month: "Mes",
    quarter: "Trimestre",
    compareWith: "Comparar Con",
    geographicLevel: "Segmentación",
    commercialChain: "Cadena Comercial",
    geo: "Zona Geográfica",
    allGeos: "Todas las Zonas",
    allClusters: "Todos los Clusters Institucionales",
    allStores: "Todas las Tiendas",
    allClusterCategories: "Todas las Categorías de Cluster",
    selectGeo: "Seleccionar Zona Geográfica...",
    selectCluster: "Seleccionar Cluster...",
    selectStore: "Seleccionar Tienda...",
    selectClusterCategory: "Seleccionar Cluster Categoría...",
    cluster: "Cluster",
    store: "Tienda",
    category: "Categoría",
    period: "Período",
    comparison: "Comparación",
    granularityOptions: {
      l12w: "Últimas 12 semanas",
      l24w: "Últimas 24 semanas",
    },
    geoLevelOptions: {
      general: "General",
      cluster: "Cluster Institucional",
      clusterCategory: "Cluster Categoría",
      store: "Tienda",
    },
    clusterCategory: "Cluster Categoría",
    select: "Seleccionar",
    nextStep: "Siguiente",
    panelTitle: "Filtros",
    collapse: "Contraer filtros",
    expand: "Expandir filtros",
    noSelection: "Aún no hay filtros seleccionados",
    nextUp: "Siguiente: {{filter}}",
    selectedCount: "{{count}} seleccionados",
    noOptions: "No hay opciones para la selección actual.",
    searchOptions: "Buscar opciones...",
    noMatches: "Ninguna opción coincide con la búsqueda.",
  },
  pogSizePicker: {
    title: "Tamaño POG",
    allSizes: "Todos los tamaños",
    noPogData: "Sin datos de POG para este alcance — prueba otra tienda o categoría.",
  },
  emptyState: {
    title: "Aun no se ha generado un análisis",
    description:
      'Configure sus filtros arriba y haga clic en "Generar Análisis" para ver informacion detallada de rendimiento.',
    howToStart: "Como comenzar:",
    title2: "Listo para Generar tu Análisis",
    description2:
      'Configura tus filtros arriba y haz clic en "Generar Análisis" para desbloquear información detallada de rendimiento',
    quickStartGuide: "Guía de Inicio Rápido",
    step1Prefix2: "Define tu ",
    step1Bold2: "alcance comercial",
    step1Suffix3: "",
    step2Prefix3: "Aplica tus filtros ",
    step2Bold3: "geográficos y de tiempo",
    step2Suffix3: "",
    step3Prefix3: "Haz clic en ",
    step3Bold3: "Generar Análisis",
    step3Suffix3: " para explorar tus resultados",
    savedConfigs: "Configuraciones Guardadas",
    savedConfigsDesc: "Acceso rápido a tus ajustes",
    save: "Guardar",
    noSavedConfigs: "No hay configuraciones guardadas",
    noSavedConfigsDesc: "Guarda tus ajustes de filtros actuales para acceso rápido",
    lastUsed: "Último uso",
  },
  savedConfigs: {
    confirmDelete: {
      title: "¿Eliminar configuración guardada?",
      description: 'Se eliminará permanentemente "{{name}}". Esta acción no se puede deshacer.',
      cancel: "Cancelar",
      confirm: "Eliminar",
    },
  },
  tabs: {
    navigationLabel: "Secciones del análisis de microespacio",
    analysis: "ANÁLISIS",
    analysisSubtitle: "¿Cómo esta rindiendo y por que?",
    action: "ACCIÓN",
    actionSubtitle: "¿Qué debo hacer?",
    majorAction: "ACCIONES MAYORES",
    majorActionSubtitle: "Cambios estructurales de surtido",
    minorAction: "ACCIONES MENORES",
    minorActionSubtitle: "Altas, bajas e intercambios",
  },
  chapterWrapper: {
    chapterLabel: "CAPITULO {{number}}",
    collapse: "Colapsar",
    collapseAll: "Colapsar todo",
    expandAll: "Expandir todo",
  },
  chapterExport: {
    pdf: "PDF",
    excel: "Excel",
    errorTitle: "Error al exportar",
    errorMessage: "No se pudo generar el archivo. Inténtalo de nuevo.",
  },
  applicability: {
    pogUnresolvedBanner: {
      title: "Algunas tiendas tienen planogramas sin resolver",
      description:
        "{{count}} tienda(s) no se pueden incluir en las acciones porque sus planogramas no están resueltos: {{stores}}.",
      andMore: "y {{count}} más",
    },
  },
  storesTable: {
    title: "Tiendas en Análisis",
    subtitle: "{{count}} tiendas incluidas segun los filtros actuales",
    loading: "Cargando tiendas en análisis...",
    loadFailed: "No se pudieron cargar las tiendas en análisis.",
    retry: "Reintentar",
    pogSizeCount: "{{count}} tamaño POG",
    pogSizeCountPlural: "{{count}} tamaños POG",
    columns: {
      storeId: "ID Tienda",
      storeName: "Nombre de Tienda",
      cluster: "Cluster",
      pogSize: "Tamaño POG",
      planogram: "Planograma",
    },
    clusterStores: "Tiendas {{name}}",
    avgPog: "POG Prom: {{value}} segmentos",
    avgPogEmpty: "POG Prom: - segmentos",
  },
  actions: {
    loadFailed: "No se pudieron cargar las recomendaciones de acciones.",
    retry: "Reintentar",
  },
  chapters: {
    executiveSummary: {
      title: "Resumen Ejecutivo",
      subtitle: "Resumen de Salud",
      healthScore: "Puntuación de Salud",
      healthScoreTooltip: {
        title: "Cálculo de Puntuación de Salud",
        description:
          "La Puntuación de Salud mide el rendimiento general evaluando cada KPI contra su objetivo, ponderándolos en una puntuación final.",
        formulaLabel: "Fórmula",
        formula: "Puntuación = Σ(Puntuación_KPI × Peso) / Σ(Pesos)",
        exampleLabel: "Ejemplo",
        exampleSales: "Ventas: 95 × 30% = 28.5",
        exampleUnits: "Unidades: 105 × 25% = 26.3",
        exampleMargin: "Margen: 88 × 30% = 26.4",
        exampleDos: "DOS: 110 × 15% = 16.5",
        exampleFinal: "Final = 97.7",
        scoring:
          "Puntuación individual: (Actual / Objetivo) × 100. ≥100 = En Objetivo, 90–99 = Cerca del Objetivo, <90 = Bajo Objetivo.",
      },
      analysisLabel: "ANÁLISIS",
      metrics: {
        sales: "Ventas",
        unitMovement: "Movimiento de Unidades",
        margin: "Margen",
        inventory: "Inventario",
        dos: "DOS",
      },
      status: {
        onTarget: "En Meta",
        nearTarget: "Cerca de Meta",
        belowTarget: "Bajo Meta",
      },
      analysis: {
        excellent: "Excelente desempeño.",
        showingStrong: "mostrando resultados sólidos",
        wellPositioned: "La categoría está bien posicionada para crecimiento continuo.",
        nearTarget: "Desempeño cerca de la meta.",
        attentionNeeded: "Se requiere atención en",
        smallAdjustments: "Pequeños ajustes podrían llevar la categoría al nivel objetivo.",
        belowTarget: "Desempeño bajo la meta.",
        focusRequired: "Se requiere enfoque en",
        strategicInterventions: "Se recomiendan intervenciones estratégicas para cerrar brechas.",
        significantGaps: "Brechas significativas detectadas.",
        priorityActions: "Acciones prioritarias necesarias para",
        comprehensiveReview: "Se recomienda revisión integral y plan de acción correctivo.",
      },
      editWeights: "Editar Pesos",
      editWeightsDesc: "Ajustar el peso de cada métrica en el cálculo de Salud",
      editTargets: "Editar Metas",
      editTargetsDesc: "Establecer tasas de crecimiento objetivo para métricas",
      healthContribution: "Contribución a Salud",
      weight: "peso",
      score: "Puntaje",
      previous: "Anterior",
      target: "Meta",
      current: "Actual",
      progressToTarget: "Progreso hacia Meta",
      totalWeight: "Peso Total",
      weightsInfo:
        "Los pesos determinan la contribución de cada métrica a la Puntuación de Salud general. Se normalizarán automáticamente en el cálculo.",
      targetsInfo:
        "Estos porcentajes representan el crecimiento esperado vs el período de comparación. Se usan para calcular valores objetivo y determinar si el desempeño cumple las expectativas.",
      inventoryNote: "Valores negativos indican reducción objetivo en Días de Inventario",
      cancel: "Cancelar",
      saveWeights: "Guardar Pesos",
      saveTargets: "Guardar Metas",
    },
    metricDeepDive: {
      title: "Métricas en Profundidad",
      subtitle: "Tendencias KPI",
      summaryText:
        "El rendimiento comercial muestra impulso positivo con el valor de ventas creciendo {{salesGrowth}} y el margen bruto expandiendose {{marginGrowth}}. La salud del inventario se mantiene optima en {{doi}} DOI.",
      metricLabels: {
        salesValue: "Valor de Ventas",
        unitsSold: "Unidades Vendidas",
        grossMargin: "Margen Bruto",
        activeSkuCount: "SKUs Activos",
        inventoryTurnover: "Rotacion de Inventario",
        daysOfInventory: "Dias de Inventario",
        outOfStock: "Agotado (%)",
        pareto: "Pareto 80/20",
      },
      kpiTooltips: {
        salesValue: "Ingreso total generado por ventas de productos en el periodo seleccionado.",
        unitsSold:
          "Número total de unidades vendidas de todos los SKUs de la categoría durante el periodo seleccionado.",
        grossMargin:
          "Utilidad bruta como porcentaje de las ventas. Calculado como (Ventas − Costo) / Ventas × 100.",
        activeSkuCount:
          "Número de SKUs distintos con al menos una unidad vendida en el periodo seleccionado.",
        inventoryTurnover:
          "Cuántas veces se vende y repone el inventario por año. Valores más altos indican una gestión de inventario más eficiente.",
        daysOfInventory:
          "Número promedio de días de stock disponible según la tasa de venta actual. Valores menores indican inventario de mayor rotación.",
        outOfStock:
          "Porcentaje de combinaciones SKU-tienda sin unidades disponibles. Valores menores indican mejor disponibilidad de producto.",
        pareto:
          "Porcentaje de SKUs que generan el 80% de las ventas totales. Valores menores indican un surtido más concentrado y eficiente.",
      },
      chartTitle: "Tendencia de Rendimiento de Categoría",
      chartTooltip: "Muestra la evolucion de ventas comparando el periodo actual vs el anterior.",
      currentPeriod: "Periodo Actual",
      periodTrend: "Tendencia del Período",
      previousYear: "Año Anterior",
      previousPeriod: "Periodo Anterior",
      currentPeriodGrowth: "Crecimiento Periodo Actual",
      previousGrowth: "Crecimiento {{name}}",
      targetLegend: "Objetivo (+{{percent}}%)",
      vsTarget: "vs Objetivo (+{{percent}}%)",
      units: {
        days: "{{value}} días",
        skus: "{{value}} SKUs",
      },
      growthBridge: {
        title: "Análisis Puente de Crecimiento",
        description:
          "El crecimiento de <strong>+3.2%</strong> es impulsado por <strong>Volumen (+2.8%)</strong> mientras que el precio contribuye poco (+0.4%).",
        tooltip:
          "Muestra como crecio la categoría, desglosado por efectos de Precio, Volumen y Mix.",
        noData: "No hay datos de puente de crecimiento disponibles.",
        priceEffect: "Efecto Precio",
        volumeEffect: "Movimiento de Unidades",
        mixEffect: "Efecto Mix",
        ofGrowth: "{{percent}}% del crecimiento",
        neutral: "Neutral",
        axisSales: "Ventas ($K)",
      },
      narrative: {
        commercialPerformance: "El rendimiento comercial muestra {{tone}} con {{details}}.",
        tonePositive: "impulso positivo",
        toneNegative: "presión a la baja",
        toneMixed: "resultados mixtos",
        salesGrowing: "valor de ventas creciendo {{delta}} a {{value}}",
        salesDeclining: "valor de ventas declinando {{delta}} a {{value}}",
        salesFlat: "ventas en {{value}}",
        marginExpanding: "margen bruto expandiéndose {{delta}} a {{value}}",
        marginContracting: "margen bruto contrayéndose {{delta}} a {{value}}",
        marginStable: "margen bruto en {{value}}",
        spaceEfficiency:
          " El Índice Espacio/Ventas se sitúa en {{value}}, indicando {{efficiency}} en el surtido.",
        efficiencyStrong: "alta productividad de espacio",
        efficiencyModerate: "eficiencia de espacio moderada",
        efficiencyWeak: "bajo rendimiento de espacio",
        conjunction: "y",
      },
      kpiCorrelation: {
        title: "Matriz de Correlación KPI",
        tooltip:
          "Muestra la relación estadística entre indicadores clave de rendimiento. Valores |r| superiores a 0.7 indican una correlación fuerte.",
        correlationStrength: "Fuerza de Correlación",
        positive: "Positiva",
        moderateNegative: "Negativa Moderada",
        strongNegative: "Negativa Fuerte",
        strongThreshold: "|r| > 0.7 = Fuerte",
        kpiLabels: {
          space_share: "Participación Espacio",
          sales_share: "Participación Ventas",
          facings: "Frentes",
          sales_pesos: "Ventas Pesos",
          units: "Unidades",
          space_to_sales_index: "Índice Espacio/Ventas",
          gm_pesos: "Margen Bruto Pesos",
        },
      },
    },
    decomposition: {
      title: "Descomposición por Segmentación",
      subtitle: "Análisis por Segmentación",
      description:
        "La asignación de espacio entre {{entity}} revela diferencias de eficiencia. Compara la participación en ventas contra la participación en espacio para identificar grupos sobre y bajo rendimiento.",
      entityPlural: {
        cluster: "clusters",
        store: "tiendas",
        categoryCluster: "clusters de categoría",
      },
      linearSpace: "Espacio Lineal por {{entity}}",
      salesPercent: "Ventas %",
      spacePercent: "Espacio %",
      efficiency: "Eficiencia: {{value}}x",
      columns: {
        cluster: "Cluster",
        store: "Tienda",
        categoryCluster: "Cluster de Categoría",
        sales: "Ventas",
        units: "Unidades",
        margin: "Margen",
        growth: "Crecimiento",
        linear: "Lineal",
        efficiencyCol: "Eficiencia",
      },
      allocationBaseline: "Base de asignación",
      allocationBaselineDesc:
        "El espacio lineal total es {{space}} en {{count}} {{entity}} con {{sales}} en ventas totales.",
      waterfallTitle: "Análisis de Cascada de Contribución al Crecimiento",
      waterfallNoData: "No hay datos de cascada disponibles para los filtros seleccionados",
      waterfallAxisLabel: "Contribución al Crecimiento (%)",
      tooltipContribution: "Contribución",
      tooltipCumulative: "Acumulado",
      ofGrowth: "{{percent}}% del crecimiento",
      totalLabel: "Total",
      totalGrowth: "Crecimiento total",
      positiveLabel: "Positiva:",
      negativeLabel: "Negativa:",
      netLabel: "Neta:",
      strategicInsight: "Perspectiva Estratégica",
      scatterTitle: "Dinámica de Rendimiento: Volumen de Ventas vs Tasa de Crecimiento",
      scatterNoData: "No hay datos de dispersión disponibles para los filtros seleccionados",
      scatterAxisSales: "Ventas ($K)",
      scatterAxisGrowth: "Crecimiento %",
      tooltipSales: "Ventas",
      tooltipGrowth: "Crecimiento",
      tooltipMargin: "Margen",
      tooltipUnits: "Unidades",
      heatmapTitle:
        "Brechas de Asignación de Espacio y Oportunidades de Optimización (Espacio vs Venta)",
      heatmapNoData: "No hay datos de brechas de asignación de espacio disponibles",
      heatmapDefaultDesc:
        "Compara participación de espacio vs participación de venta por cluster. Celdas verdes indican sub-asignación (espacio < venta — oportunidad de expansión), celdas rojas indican sobre-asignación (espacio > venta — oportunidad de reducción).",
      viewBy: "Ver por",
      toggleBrand: "Marca",
      toggleSubcategory: "Subcategoría",
      actionableRecommendation: "Recomendación Accionable",
      actionableRecommendationText:
        "Reducir la asignación de espacio para marcas sobre-espaciadas (zonas rojas) mientras se expanden marcas sub-espaciadas en formatos clave (zonas verdes). Esta reasignación sin costo requiere solo ajustes de planograma y puede generar ventas incrementales de categoría.",
      chartTooltips: {
        linearSpace:
          "Distribución actual de espacio en anaquel por formato de tienda. Representa el espacio lineal total disponible para la categoría en todas las ubicaciones del análisis.",
        waterfall:
          "Contribución incremental al crecimiento por formato de cluster. Cada barra representa los puntos porcentuales de aportación al rendimiento total de la categoría, revelando qué formatos impulsan el momentum del negocio.",
        scatter:
          "Eje X: Ventas Totales | Eje Y: Crecimiento % | Tamaño burbuja: Margen %. Cuadrante superior derecho = rendimiento óptimo (altas ventas + alto crecimiento). Burbujas más grandes indican mejor margen.",
        heatmap:
          "Brechas de asignación de espacio entre formatos de tienda. Celdas verdes indican sub-asignación (oportunidad de expansión), celdas rojas indican sobre-asignación (oportunidad de reducción). Los valores representan puntos porcentuales por encima o debajo de la asignación óptima.",
      },
      heatmap: {
        title: "Mapa de Calor de Brechas de Espacio",
        description:
          "Brechas de rendimiento por marca y cluster. Verde = sub-espaciado (oportunidad), Rojo = sobre-espaciado (reequilibrar).",
        premium: "Premium",
        urban: "Urbano",
        suburban: "Suburbano",
      },
      legend: {
        veryUnderSpaced: "Muy Sub-espaciado",
        underSpaced: "Sub-espaciado",
        slightlyUnder: "Levemente Sub",
        balanced: "Equilibrado",
        slightlyOver: "Levemente Sobre",
        overSpaced: "Sobre-espaciado",
        veryOverSpaced: "Muy Sobre-espaciado",
      },
      narrative: {
        descriptionSingle: '"{{cluster}}" representa el 100% de ventas y espacio.',
        description:
          "La asignación de espacio entre {{entity}} revela diferencias de eficiencia. {{most}} genera {{mostSales}}% de ventas con {{mostSpace}}% de espacio ({{mostEff}}pp de eficiencia), mientras que {{least}} muestra {{leastEff}}pp{{rebalance}}.",
        rebalanceHint: " — una oportunidad de reequilibrio",
        waterfallDesc:
          "{{top}} impulsa {{pp}}pp de crecimiento ({{pct}}% de la ganancia total), seguido por {{rest}}. La distribución revela disparidades de eficiencia entre {{entity}}.",
        waterfallFollowed: "{{cluster}} con {{pp}}pp",
        waterfallInsight:
          "El formato {{top}} representa {{spacePct}}% del espacio total pero entrega {{growthPct}}% del crecimiento total, demostrando productividad superior de espacio. Cada metro lineal en {{top}} genera retornos desproporcionadamente mayores que otros formatos.",
        scatterShows:
          "{{cluster}} muestra {{growth}} de crecimiento con {{margin}}% de margen y {{sales}} en ventas",
        insightGrowthLeader: "Líder de crecimiento con fuerte rendimiento de margen",
        insightVolumeLeader: "Líder de volumen con ventaja de escala",
        insightUnderperforming: "Bajo rendimiento — requiere reasignación de espacio",
        insightStable: "Rendimiento estable con oportunidad de expansión",
        heatmapDesc:
          "{{under}} muestra sub-asignación crítica ({{underGap}}pp brecha promedio), mientras que {{over}} demuestra sobre-asignación consistente ({{overGap}}pp). Redistribuir espacio de sobre-espaciados a sub-espaciados en {{entity}} crea valor inmediato.",
        brands: "marcas",
        subcategories: "subcategorías",
      },
    },
    brandAnalysis: {
      title: "Detalle por Subcategoría y Marca",
      subtitle: "Mix de Marcas",
      spaceEfficiencyTitle: "Eficiencia de Espacio",
      spaceEfficiencySubtitle:
        "¿Cómo se alinea el espacio en anaquel con el rendimiento de ventas?",
      gapAnalysisTitle: "Análisis de Brechas",
      contributionToGrowthTitle: "Contribución al Crecimiento",
      contributionToGrowthSubtitle: "Qué ítems impulsan o frenan el rendimiento de la categoría",
      growthDriversAndGapsTitle: "Impulsores y Detractores de Crecimiento",
      growthDriversSubtitle: "Qué ítems aceleran el crecimiento y cuáles frenan la categoría",
      performanceShiftTitle: "Cambio de Rendimiento",
      performanceShiftSubtitle: "Cómo está cambiando el mix de ventas entre ítems",
      subcategorySpaceEfficiency: "1. Eficiencia de Espacio por Subcategoría",
      subcategorySpaceEfficiencySubtitle:
        "¿Cómo se alinea el espacio en anaquel con el rendimiento de ventas?",
      subcategoryGapAnalysis: "Análisis de Brechas por Subcategoría",
      brandSpaceEfficiency: "2. Eficiencia de Espacio por Marca",
      brandSpaceEfficiencySubtitle:
        "¿Qué marcas merecen más o menos espacio en anaquel según ventas?",
      brandGapAnalysis: "Análisis de Brechas por Marca",
      contributionToGrowthBrand: "Contribución al Crecimiento por Marca",
      contributionToGrowthSubcategory: "Contribución al Crecimiento por Subcategoría",
      contributionToGrowthSubtitleBrand:
        "Qué marcas impulsan o frenan el rendimiento de la categoría",
      contributionToGrowthSubtitleSubcategory:
        "Qué subcategorías contribuyen al crecimiento de la categoría",
      toggleBrand: "Marca",
      toggleSubcategory: "Subcategoría",
      growthDriversAndGapsBrand: "Impulsores y Detractores de Crecimiento por Marca",
      growthDriversAndGapsSubcategory: "Impulsores y Detractores de Crecimiento por Subcategoría",
      growthDriversSubtitleBrand: "Qué marcas aceleran el crecimiento y cuáles frenan la categoría",
      growthDriversSubtitleSubcategory:
        "Qué subcategorías impulsan la expansión y cuáles están en declive",
      growthDrivers: "Impulsores de Crecimiento",
      topPerformers: "Mejores Rendimientos",
      performanceGaps: "Brechas de Rendimiento",
      biggestDecliners: "Mayores Declives",
      performanceShiftSubcategory: "Cambio de Rendimiento por Subcategoría",
      performanceShiftBrand: "Cambio de Rendimiento por Marca",
      performanceShiftSubtitleSubcategory:
        "Evolución de preferencias del consumidor entre tipos de salsa",
      performanceShiftSubtitleBrand:
        "Cambios de participación de mercado entre marcas competidoras",
      previousPeriodLegend: "Periodo Anterior",
      currentPeriodLegend: "Periodo Actual",
      tooltipScatterPlot:
        "Puntos sobre la línea están sub-espaciados (necesitan más espacio). Puntos bajo la línea están sobre-espaciados (reducir espacio). Puntos en la línea están perfectamente balanceados.",
      tooltipContribution:
        "Muestra la contribución incremental de ventas. Barras verdes indican impulsores de crecimiento, barras rojas muestran ítems en declive.",
      tooltipDriversGaps:
        "Identifica qué ítems aceleran el crecimiento y cuáles frenan la categoría, con cuantificación del impacto.",
      tooltipPerformanceShift:
        "Muestra cómo el mix de ventas está cambiando entre ítems, revelando tendencias emergentes del consumidor.",
      noData: "No hay datos de marca o subcategoría disponibles para esta categoría.",
      noDataSection: "Sin datos disponibles.",
      filterTopN: "Mostrar",
      topNAll: "Todos",
      topNCount: "Top {{count}}",
      filterGroups: "Grupos",
      filterGroupsPlaceholder: "Todos los grupos",
      filterGroupsAll: "Todos los grupos",
      contributionAxisSales: "Cambio de Ventas ($K)",
      contributionLabel: "Contribución",
      positiveLabel: "Positiva:",
      negativeLabel: "Negativa:",
      netLabel: "Neta:",
      netItem: "Neto",
      metricLabels: {
        sales: "Ventas",
        units: "Unidades",
        margin: "Margen",
      },
    },
    productLevel: {
      title: "Rendimiento a Nivel de Producto",
      subtitle: "Análisis SKU",
      newLaunches: "MAYOR VENTA",
      newLaunchesValue: "+$25.7K de 3 SKUs",
      newLaunchesDesc: "Contribuyendo 1.4% de crecimiento incremental",
      discontinued: "MENOR VENTA",
      discontinuedValue: "-$27.8K de 2 SKUs",
      discontinuedDesc: "Productos de bajo rendimiento salieron del surtido",
      skuPerformanceMatrix: "Matriz de Rendimiento SKU — Gráfico de Burbujas",
      skuMatrixSubtitle:
        "Evalúe cada SKU en ventas, margen y volumen para identificar Ganadores, Impulsores de Tráfico, Oportunidades y candidatos a Salida",
      skuMatrixTooltipTitle: "Cómo Funciona la Clasificación por Cuadrantes",
      skuMatrixTooltipText:
        "Los SKUs se clasifican en cuadrantes según su posición relativa a los valores medianos de las métricas seleccionadas. Ganadores: sobre la mediana en ambos ejes. Impulsores: sobre X, bajo Y. Oportunidades: bajo X, sobre Y. Revisión/Salida: bajo ambos.",
      filterBy: "Filtrar por",
      filterAll: "Todos",
      filterBrand: "Marca",
      filterSubcategory: "Subcategoría",
      selectBrand: "Seleccionar marca...",
      selectSubcategory: "Seleccionar subcategoría...",
      chartConfig: "Configuración de Gráfico",
      resetToDefault: "Restablecer",
      xAxisMetric: "Métrica Eje X",
      yAxisMetric: "Métrica Eje Y",
      bubbleSize: "Tamaño de Burbuja",
      axisOptions: {
        sales: "Valor de Ventas ($)",
        margin: "Margen Bruto (%)",
        units: "Unidades Vendidas",
      },
      axisLabels: {
        sales: "Valor de Ventas ($K)",
        margin: "Margen Bruto (%)",
        units: "Unidades Vendidas",
        salesIndex: "Indice de Ventas",
        marginIndex: "Indice de Margen",
      },
      quadrantTitle: "Clasificación por Cuadrantes y Análisis de Causa Raíz",
      quadrants: {
        winners: "Ganadores",
        winnersDesc: "Altas ventas y alto margen",
        trafficDrivers: "Impulsores de Tráfico",
        trafficDriversDesc: "Altas ventas, menor margen",
        opportunities: "Oportunidades",
        opportunitiesDesc: "Alto margen, bajas ventas",
        reviewExit: "Revisión / Salida",
        reviewExitDesc: "Bajo en ambos ejes",
      },
      tooltipLabels: {
        sales: "Ventas",
        margin: "Margen",
        units: "Unidades",
      },
      skusCount: "({{count}} SKUs)",
      penetration: {
        title: "Matriz de Distribución y Desempeño de SKU",
        subtitle:
          "Cada punto es un SKU, ubicado por distribución en tiendas y velocidad de venta por tienda. Tamaño de burbuja = ingresos.",
        tooltip:
          "Para cada SKU: en cuántas tiendas está presente (X) frente a las unidades promedio vendidas por tienda (Y). El tamaño de la burbuja son los ingresos totales. Las líneas de umbral son las medianas de la categoría; los colores agrupan los SKU por cuartil de velocidad de venta.",
        noData: "Sin datos de distribución de SKU disponibles.",
        toastTitle: "Cap. 5 · Penetración",
        loadError: "No se pudieron cargar los datos de penetración — no se muestran datos",
        axisX: "Número de Tiendas con el SKU",
        axisY: "Venta por Tienda (unidades)",
        distributionThreshold: "Umbral de Distribución",
        performanceThreshold: "Umbral de Desempeño",
        skusCount: "{{count}} SKUs",
        tiers: {
          high: "Alto Desempeño",
          good: "Bueno",
          average: "Promedio",
          low: "Bajo",
        },
        tierRange: {
          high: "≥ {{min}} u/tienda",
          band: "{{min}}–{{max}} u/tienda",
          low: "< {{max}} u/tienda",
        },
        tooltipLabels: {
          brand: "Marca",
          stores: "Tiendas",
          unitsPerStore: "Unidades/tienda",
          revenue: "Ingresos",
        },
        quadrantTitle: {
          star: "⭐ SKU Estrella",
          expansion: "📈 Oportunidades de Expansión",
          rationalize: "⚠️ Candidatos a Racionalización",
          test: "🧪 Probar y Aprender",
        },
        quadrantDesc: {
          star: "Alta distribución + alta velocidad. Ganadores de la categoría: proteger espacio e inventario.",
          expansion:
            "Fuerte velocidad por tienda pero distribución limitada. Expandir a más tiendas.",
          rationalize:
            "Amplia distribución pero baja velocidad. Revisar para optimización o baja del SKU.",
          test: "Distribución y velocidad limitadas. Monitorear SKU de especialidad o prueba regional.",
        },
      },
      cpiRanking: {
        title: "Ranking CPI vs Periodo Anterior",
        subtitle:
          "Rastree el momentum de productos para identificar estrellas en ascenso y artículos en declive antes de que se conviertan en problemas críticos",
        description: "Indice de Rendimiento Combinado — ponderado por Ventas, Margen y Rotacion.",
        tooltipTitle: "Ranking CPI",
        tooltipText:
          "CPI (Índice de Rendimiento de Categoría) es un puntaje compuesto que combina ventas, margen y rotación. Flechas verdes indican productos mejorando, flechas rojas muestran productos en declive. Edite pesos para simular escenarios.",
        cpiWeightsTitle: "Pesos de Componentes CPI",
        salesWeight: "Peso Ventas",
        marginWeight: "Peso Margen",
        rotationWeight: "Peso Rotación",
        total: "Total",
        mustEqual100: "(debe ser 100%)",
        recalculate: "Recalcular CPI",
        narrative: {
          contributing: "Contribuyendo {{amount}} de {{count}} SKU",
          contributingPlural: "Contribuyendo {{amount}} de {{count}} SKUs",
          noNewLaunches: "No se detectaron nuevos lanzamientos en este período",
          flaggedForReview: "{{count}} SKUs marcados para revisión",
          lowSalesMargin: "Bajas ventas + bajo margen — candidatos a eliminación",
          rankDetail: "Posición #{{rank}}",
          reviewQuadrant: "Cuadrante de revisión",
        },
        columns: {
          rank: "#",
          sku: "SKU",
          cpi: "CPI",
          spacePercent: "Espacio %",
          status: "Estado",
          prev: "Ant.",
          chg: "Var.",
          sales: "Ventas",
          margin: "Margen",
          rotation: "Uds.",
        },
      },
    },
    actionRecommendations: {
      title: "Recomendaciones de Acción",
      subtitle: "Insights Accionables",
      description:
        "Basado en un análisis integral, hemos identificado <strong>oportunidades clave</strong> entre clusters. Haga clic en las recomendaciones a continuacion para aplicarlas automaticamente en el <strong>Capitulo 2: Acciones de Surtido y Espacio</strong>.",
      impactLabel: "Impacto:",
      skuActions: "{{count}} acciones SKU",
      applied: "Aplicado",
      accept: "Aceptar",
      noData: "No hay recomendaciones de acción disponibles para esta categoría.",
    },
    actionDetail: {
      title: "Detalle de Acción",
      subtitle: "Decisiones SKU",
      summary: {
        accepted: "Aceptados",
        rejected: "Rechazados",
        pending: "Pendientes",
        estImpact: "Impacto Est.",
      },
      filterByCluster: "Filtrar por Cluster:",
      allClusters: "Todos los Clusters",
      skuRecommendations: "Recomendaciones SKU",
      columns: {
        sku: "SKU",
        brand: "Marca",
        subcategory: "Subcategoría",
        segment: "Segmento",
        cpi: "CPI",
        quadrant: "Cuadrante",
        cluster: "Cluster",
        store: "Tienda",
        recommendation: "Recomendacion",
        rationale: "Justificacion",
        decision: "Decision",
      },
      decisions: {
        yes: "Si",
        accept: "Aceptar",
        no: "No",
        reject: "Rechazar",
        watch: "Monitorear",
        noAction: "Sin acción",
        accepted: "Aceptado",
        rejected: "Rechazado",
        pending: "Pendiente",
      },
      quadrants: {
        winner: "Ganador",
        opportunity: "Oportunidad",
        traffic: "Tráfico",
        review: "Revisión",
      },
      recommendations: {
        expand: "EXPANDIR",
        reduce: "REDUCIR",
        flagged: "MARCADO (Revisión)",
        protect: "PROTEGER",
        monitor: "MONITOREAR",
        keepAsIs: "MANTENER IGUAL",
        expandFacing: "EXPANDIR +{{count}} frente",
        expandFacings: "EXPANDIR +{{count}} frentes",
        reduceFacing: "REDUCIR {{count}} frente",
        reduceFacings: "REDUCIR {{count}} frentes",
      },
      clearFilters: "Limpiar filtros",
      selected: "seleccionados",
      acceptSelected: "Aceptar Seleccionados",
      rejectSelected: "Rechazar Seleccionados",
      viewOnly: "Solo lectura",
      notActionable: "No accionable",
      noCh1Selection: "No hay recomendaciones aceptadas en el Capítulo 1",
      noCh1SelectionHint:
        "Acepta una o más recomendaciones de acción arriba para revisar las decisiones individuales de SKU aquí.",
    },
    inventoryTable: {
      title: "Recomendaciones de Inventario",
      noData: "No hay recomendaciones de inventario disponibles para esta categoría.",
      summary: {
        unitsToAdd: "Unidades a Agregar",
        unitsToRemove: "Unidades a Retirar",
        netChange: "Cambio Neto",
        highPriority: "Alta Prioridad",
      },
      columns: {
        sku: "SKU",
        brand: "Marca",
        cluster: "Cluster",
        store: "Tienda",
        current: "Actual",
        target: "Objetivo",
        change: "Cambio",
        doi: "DOI",
        oosRisk: "Riesgo OOS",
        action: "Acción",
        priority: "Prioridad",
      },
      values: {
        keep: "MANTENER",
        low: "Bajo",
        medium: "Medio",
        high: "Alto",
        overstock: "Sobrestock",
      },
    },
    spaceAllocation: {
      title: "Asignacion de Espacio Lineal por Cluster",
      noData: "No hay datos de asignación de espacio disponibles para esta categoría.",
      storesAvg: "{{stores}} tiendas · {{avg}}cm prom",
      before: "Antes",
      after: "Despues",
      legend: {
        winners: "Ganadores",
        opportunities: "Oportunidades",
        traffic: "Trafico",
        review: "Revision",
      },
    },
    reviewSimulate: {
      title: "Revisar y Simular",
      subtitle: "Ejecutar",
      description:
        "Ha aceptado <strong>{{count}}</strong> recomendaciones SKU del Capitulo 2. Revise el impacto consolidado a continuacion, simule los cambios en el modulo de Simulación de Surtido, o exporte el plan de acción completo para revision offline y aprobacion de interesados.",
      acceptedTitle: "Recomendaciones Aceptadas del Capitulo 2",
      expandAction: "Expandir {{count}} SKU",
      expandActionPlural: "Expandir {{count}} SKUs",
      reduceAction: "Reducir {{count}} SKU",
      reduceActionPlural: "Reducir {{count}} SKUs",
      flaggedAction: "{{count}} SKU marcado",
      flaggedActionPlural: "{{count}} SKUs marcados",
      flaggedSuffix: " para revision",
      noActionsTitle: "Sin Acciones Aceptadas",
      noActionsDescription:
        "Acepte recomendaciones en el Capitulo 2 para revisar y simular el impacto.",
      projectedImpact: "Impacto Proyectado",
      kpiColumns: {
        kpi: "KPI",
        currentBefore: "Actual (Antes)",
        projectedAfter: "Proyectado (Despues)",
        change: "Cambio",
      },
      kpiRows: {
        salesValue: "Valor de Ventas",
        grossMarginPercent: "Margen Bruto %",
        spaceEfficiency: "Eficiencia de Espacio",
        bottomSkusPercent: "SKUs Inferiores %",
        skuProductivity: "Productividad SKU ($/SKU)",
        categoryHealthScore: "Puntuacion de Salud de Categoría",
      },
      catalogOnlyDisclaimer:
        "Nota: los números pueden ser negativos porque algunos productos AGREGADOS provienen del catálogo sin historial de ventas — su ganancia proyectada no se simula.",
    },
  },
  minorActions: {
    chapter1Title: "Recomendaciones de Acciones Menores",
    chapter1Subtitle: "Resumen por Cluster",
    chapter1SubtitleStore: "Resumen por Tienda",
    chapter1SubtitleClusterCategory: "Resumen por Cluster Categoría",
    chapter1Description:
      "Revise y apruebe ajustes de surtido por cluster. Estos cambios optimizan el mix a traves de adiciones, eliminaciones y ajustes de frentes basados en datos de rendimiento y patrones de demanda.",
    chapter1DescriptionStore:
      "Revise y apruebe ajustes de surtido por tienda. Estos cambios optimizan el mix a traves de adiciones, eliminaciones y ajustes de frentes basados en datos de rendimiento y patrones de demanda.",
    chapter2Title: "Detalle de Acciones",
    chapter2Subtitle: "Recomendaciones Individuales",
    chapter3Title: "Revisar y Simular",
    chapter3Subtitle: "Ejecutar",
    chapter3Description:
      "Ha aceptado {{count}} recomendaciones de acciones menores. Revise el impacto consolidado y genere una simulación de surtido o exporte el plan de acción.",
    recommendations: "recomendaciones",
    products: "productos",
    stores: "tiendas",
    accepted: "Aceptadas",
    rejected: "Rechazadas",
    pending: "Pendientes",
    acceptAll: "Aceptar Todo",
    rejectAll: "Rechazar Todo",
    acceptedActionsTitle: "Acciones Menores Aceptadas",
    acceptedSummary: {
      counts:
        "{{addCount}} agregados · {{removeCount}} removidos · {{expandCount}} expandidos · {{reduceCount}} reducidos",
    },
    noActionsTitle: "Sin Recomendaciones Aceptadas",
    noActionsDescription:
      "Acepte recomendaciones en el Capitulo 1 o Capitulo 2 para revisar el impacto proyectado.",
    rationale: {
      fsi: "FSI",
      cpi: "CPI",
      space: "Espacio",
      catalogNoSales: "Producto sin presencia en planogramas",
    },
    removalsColumn: "Eliminaciones",
    addsColumn: "Adiciones",
    adjustmentsColumn: "Ajustes de Frentes",
    actionLabels: {
      add: "AGREGAR",
      remove: "ELIMINAR",
      adjust: "AJUSTAR",
      expand: "EXPANDIR",
      reduce: "REDUCIR",
    },
    addSource: {
      sameCluster: "Mismo clúster",
      chainWide: "Toda la cadena",
      catalogNoSales: "Solo catálogo",
    },
    detailTooltip: {
      sku: "SKU",
      fsi: "FSI",
      cpi: "CPI",
      space: "Espacio",
      projectedImpact: "Impacto proyectado",
      currentFacings: "Frentes actuales",
      recommendedFacings: "Frentes a agregar",
      targetFacings: "Frentes objetivo",
      facingChange: "Cambio de frentes",
      width: "Ancho",
      subcategory: "Subcategoría",
      brand: "Marca",
      segment: "Segmento",
      size: "Tamaño",
    },
    noPlanogramSectionTitle: "Sin planograma para las tiendas",
  },
  charts: {
    scatterPlot: {
      xLabel: "Asignacion de Espacio (%)",
      yLabel: "Contribucion de Ventas (%)",
      tooltipSpace: "Espacio: {{value}}%",
      tooltipSales: "Ventas: {{value}}%",
      tooltipGap: "Brecha: {{value}}pp",
      underSpaced: "Sub-espaciado",
      overSpaced: "Sobre-espaciado",
    },
    gapAnalysis: {
      columns: {
        name: "Nombre",
        spacePercent: "Espacio %",
        salesPercent: "Ventas %",
        gap: "Brecha",
        status: "Estado",
      },
      statuses: {
        underSpaced: "Sub-espaciado",
        overSpaced: "Sobre-espaciado",
        balanced: "Equilibrado",
      },
    },
  },
  clusterCategoryConfig: {
    title: "Configuración de Cluster de Categoría",
    subtitle: "Configura agrupaciones de tiendas por categoría",
    loadingTitle: "Cargando configuración de cluster de categoría",
    loadingDescription: "Obteniendo agrupaciones guardadas de clusters para cada cadena.",
    chainCategories: "Categorías {{chain}}",
    searchCategoriesPlaceholder: "Buscar categorías por nombre...",
    columns: {
      category: "CATEGORÍA",
      status: "ESTADO",
      clusters: "CLUSTERS",
      stores: "TIENDAS",
    },
    configured: "Configurado",
    notConfigured: "Sin configurar",
    noCategoriesMatch: "Ninguna categoría coincide con tu búsqueda",
    headerSubtitle: "{{chain}} — Configuración de Cluster de Categoría",
    saveConfiguration: "Guardar Configuración",
    availableStores: "Tiendas Disponibles",
    chainStores: "Tiendas {{chain}}",
    searchStoresPlaceholder: "Buscar por nombre o ID...",
    storesSelected: "{{count}} tienda seleccionada",
    storesSelectedPlural: "{{count}} tiendas seleccionadas",
    clear: "Limpiar",
    allAssigned: "Todas las tiendas están asignadas a clusters",
    noStoresMatch: "Ninguna tienda coincide con tu búsqueda",
    createdClusters: "Clusters Creados ({{count}})",
    newCluster: "Nuevo Cluster",
    clusterNamePlaceholder: "Nombre del cluster...",
    editName: "Editar nombre",
    create: "Crear",
    addCount: "+ Agregar {{count}}",
    storesCount: "{{count}} tiendas",
    noClusters: "Aún no se han creado clusters",
    noClustersHint: 'Haz clic en "Nuevo Cluster" para comenzar',
    storeMeta: "Tienda {{id}} – {{geo}} – {{banner}}",
    storeMetaWithPog: "Tienda {{id}} – Tamaño POG: {{pogSize}} – {{geo}} – {{banner}}",
    pogSizeNoData: "Sin Datos",
  },
  notifications: {
    sampleDataTitle: "Usando datos de muestra",
    filtersLoadFailed: "No se pudieron cargar los filtros desde la API",
    executiveHealthTitle: "Cap. 1 · Puntuación de Salud",
    executiveHealthEmpty:
      "No hay datos de salud de categoría disponibles — se muestran datos de muestra",
    executiveHealthFailed:
      "No se pudieron cargar los datos de salud — se muestran datos de muestra",
    metricKpiTitle: "Cap. 2 · Datos KPI",
    metricKpiFailed: "No se pudieron cargar los datos KPI — no se muestran datos",
    growthBridgeTitle: "Cap. 2 · Puente de Crecimiento",
    growthBridgeFailed:
      "No se pudo cargar la cascada de crecimiento — se muestran datos de muestra",
    clusterSummaryTitle: "Cap. 3 · Resumen de Clusters",
    clusterSummaryFailed: "No se pudo cargar el resumen de clusters — no se muestran datos",
    clusterSummaryEmpty: "No hay datos de clusters disponibles — se muestran datos de muestra",
    clusterWaterfallTitle: "Cap. 3 · Cascada de Clusters",
    clusterWaterfallFailed: "No se pudo cargar la cascada de clusters — no se muestran datos",
    clusterScatterTitle: "Cap. 3 · Dispersión de Clusters",
    clusterScatterFailed: "No se pudo cargar la dispersión de clusters — no se muestran datos",
    skuMatrixTitle: "Cap. 5 · Matriz SKU",
    skuMatrixFailed: "No se pudo cargar la matriz SKU — no se muestran datos",
    cpiRankingTitle: "Cap. 5 · Ranking CPI",
    cpiRankingFailed: "No se pudo cargar el ranking CPI — no se muestran datos",
    cpiRecalculationFailed: "No se pudo recalcular el CPI",
  },
  errors: {
    simulationFailed: "Error en Simulación",
    simulationFailedMessage:
      "No se pudo generar la simulación de surtido. Por favor intente de nuevo.",
    clusterSaveFailed: "Error al Guardar",
    clusterSaveFailedMessage:
      "No se pudo guardar la configuración de clusters. Por favor intente de nuevo.",
    configSaveFailed: "Error al Guardar",
    configSaveFailedMessage: "No se pudo guardar la configuración. Por favor intente de nuevo.",
    configDeleteFailed: "Error al Eliminar",
    configDeleteFailedMessage: "No se pudo eliminar la configuración. Por favor intente de nuevo.",
    configLoadFailed: "Error al Cargar",
    configLoadFailedMessage: "No se pudieron cargar las configuraciones guardadas.",
  },
};
